import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import FormField from '@components/Common/FormField';
import KpiTile from '@components/Common/KpiTile';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { chartColor } from '@components/Common/Charts/chartPalette';
import { candidateCityBreakdownQuery } from '@constants/graphQLQueries';
import { formatDate, isoDaysAgo } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FlightLandOutlinedIcon from '@mui/icons-material/FlightLandOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { Box, Button, Card, CardContent, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface KV { key: string; value: string }

interface Breakdown {
  city: string;
  total: number;
  livesThere: number;
  prefersOnly: number;
  /*
   * Sourced by us and never claimed, against registered. Optional on the type because a backend
   * that predates them returns neither, and a tile reading "NaN" is worse than no tile.
   */
  external?: number | null;
  internal?: number | null;
  byArea: KV[];
  byExperience: KV[];
  byRole: KV[];
  /** Absent from a backend that predates them; read as none. */
  byPreferredLocality?: KV[] | null;
  bySkill?: KV[] | null;
}

/**
 * Cities offered. Mumbai only for now, as asked. Adding one is a line here, plus its areas in
 * Settings, so "Pune" counts Hinjewadi and Kothrud and not just "Pune".
 */
const CITY_OPTIONS = [{ value: 'Mumbai', label: 'Mumbai' }];

/**
 * When they joined, as created-at: the same date the Job Seekers list's "Created from / to"
 * filters on, so "Open these candidates" with the window still lands on this total. A sourced
 * profile claimed later keeps its import date, exactly as it does in the list.
 */
type Period = 'all' | 7 | 30 | 90 | 'custom';
const PERIODS: { value: Period; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 'custom', label: 'Custom' },
];

const num = (n: number) => n.toLocaleString('en-IN');
const counts = (rows: KV[]) => rows.map((r) => Number(r.value));
const labels = (rows: KV[]) => rows.map((r) => r.key);

/**
 * One ranked list as horizontal bars, in the dashboard's chart card.
 *
 * No legend: every chart here is a single series of candidate counts, so a "Candidates" key above
 * each one said nothing and added a row of height to every card.
 */
const RankedBars: React.FC<{
  title: string;
  note?: string;
  rows: KV[];
  color: string;
  empty: string;
}> = ({ title, note, rows, color, empty }) => (
  <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
    <CardContent>
      <Typography variant="subtitle1" className="an-chart-title">{title}</Typography>
      {note && <Typography variant="caption" className="an-chart-note">{note}</Typography>}
      {rows.length > 0 ? (
        <BarChart
          layout="horizontal"
          hideLegend
          yAxis={[{ data: labels(rows), scaleType: 'band', width: 140, tickLabelStyle: { fontSize: 11 } }]}
          series={[{ data: counts(rows), label: 'Candidates', color }]}
          height={Math.max(200, rows.length * 26)}
          margin={{ top: 8, right: 16, bottom: 24, left: 8 }}
        />
      ) : (
        <EmptyState compact title={empty} />
      )}
    </CardContent>
  </Card>
);

/**
 * Candidates in a city, broken down.
 *
 * "In the city" is the job-seeker list's City filter exactly -- lives in the city or one of its
 * configured areas, or prefers it -- so the total here and the list's total are the same number,
 * and "Open these candidates" lands on that list.
 */
const CandidatesByCity: React.FC = () => {
  const [city, setCity] = useState(CITY_OPTIONS[0].value);
  const [period, setPeriod] = useState<Period>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState<Breakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Today in UTC, the zone the backend bounds a day in; also the date pickers' max.
  const today = isoDaysAgo(0);

  /*
   * The window, or why there is none yet. A half-typed custom range is not a query: fetching on
   * it would flash all-time numbers between keystrokes, so the previous result stays put.
   */
  const { range, rangeError } = useMemo((): { range: { from?: string; to?: string } | null; rangeError: string } => {
    if (period === 'all') return { range: {}, rangeError: '' };
    if (period !== 'custom') return { range: { from: isoDaysAgo(period), to: today }, rangeError: '' };
    if (!customFrom || !customTo) return { range: null, rangeError: '' };
    if (customFrom > customTo) return { range: null, rangeError: 'From must be on or before To.' };
    return { range: { from: customFrom, to: customTo }, rangeError: '' };
  }, [period, customFrom, customTo, today]);

  useEffect(() => {
    if (!range) return undefined;
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(candidateCityBreakdownQuery(city, range))
      .then((res) => { if (live) setData(res?.data?.candidateCityBreakdown ?? null); })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [city, range]);

  const windowed = !!range && !!range.from;
  const listParams = new URLSearchParams({ filter_City: city });
  if (windowed && range?.from && range.to) {
    // The list's own params (never renamed), and it wants both or neither.
    listParams.set('date_from', range.from);
    listParams.set('date_to', range.to);
  }

  return (
    <>
      {/* The same compact control row every other dashboard tab uses: a stacked label here made
          this the one tab whose first row was nearly twice as tall. */}
      <FilterRow>
        <Box className="an-inline-group">
          <Typography variant="body2" className="an-inline-label">City</Typography>
          <SelectFormField
            required={false}
            name="breakdownCity"
            label=""
            size="small"
            value={city}
            options={CITY_OPTIONS}
            fullWidth={false}
            onChange={(_n, v) => setCity(String(v))}
            selectProps={{ inputProps: { 'aria-label': 'City' } }}
          />
        </Box>
        <Box className="an-inline-group">
          <Typography variant="body2" className="an-inline-label">Joined</Typography>
          {/* The dashboard's own period control, so the row keeps the other tabs' height. */}
          <ToggleButtonGroup
            value={period}
            exclusive
            size="small"
            aria-label="Joined"
            onChange={(_, v: Period | null) => { if (v !== null) setPeriod(v); }}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.label} value={p.value}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {period === 'custom' && (
          <>
            {/* label="" because "Joined" is the label; the accessible names are set explicitly. */}
            <FormField
              required={false}
              name="joinedFrom"
              label=""
              type="date"
              size="small"
              fullWidth={false}
              value={customFrom}
              textFieldProps={{ slotProps: { htmlInput: { max: today, 'aria-label': 'Joined from' } } }}
              onChange={(_n, v) => { if (String(v) <= today) setCustomFrom(String(v ?? '')); }}
            />
            <FormField
              required={false}
              name="joinedTo"
              label=""
              type="date"
              size="small"
              fullWidth={false}
              value={customTo}
              error={!!rangeError}
              textFieldProps={{ slotProps: { htmlInput: { max: today, 'aria-label': 'Joined to' } } }}
              onChange={(_n, v) => { if (String(v) <= today) setCustomTo(String(v ?? '')); }}
            />
          </>
        )}
        <Button
          size="small"
          variant="outlined"
          component={Link}
          to={`/jobseekers?${listParams.toString()}`}
        >
          Open these candidates
        </Button>
      </FilterRow>
      {rangeError && (
        <Typography variant="caption" role="alert" className="an-range-error">{rangeError}</Typography>
      )}

      {loading && !data && <SkeletonLoader />}
      {error && <EmptyState title="The breakdown could not be loaded" body="Try again in a moment." />}

      {data && windowed && data.total === 0 && (
        <EmptyState title={`No candidates in ${data.city} joined in this period`} body="Try a longer range." />
      )}

      {data && !(windowed && data.total === 0) && (
        <>
          {windowed && range?.from && (
            <Typography variant="caption" className="an-chart-note">
              Counting candidates who joined between {formatDate(range.from)} and {formatDate(range.to ?? today)}.
            </Typography>
          )}
          <Box className="cc-kpi-grid">
            <KpiTile label={`Candidates in ${data.city}`} value={num(data.total)} icon={<PeopleAltOutlinedIcon />} tone="primary" />
            <KpiTile label={`Live in ${data.city}`} value={num(data.livesThere)} icon={<HomeWorkOutlinedIcon />} tone="teal" />
            <KpiTile label={`Want ${data.city}, live elsewhere`} value={num(data.prefersOnly)} icon={<FlightLandOutlinedIcon />} tone="indigo" />
            {/*
              * Registered against sourced, which is the split that decides what you can do with a
              * candidate rather than a fact about where the row came from. `isExternal` is written
              * true only by the recruiting-ops create and the resume import, and set false again
              * the moment somebody claims that profile, so "Sourced by us" means precisely "we
              * found them and they have not signed up".
              *
              * Rendered only when the backend sends them: an older one returns neither, and two
              * tiles reading NaN are worse than two tiles missing.
              */}
            {typeof data.internal === 'number' && (
              <KpiTile
                label="Registered themselves"
                value={num(data.internal)}
                icon={<HowToRegOutlinedIcon />}
                tone="success"
              />
            )}
            {typeof data.external === 'number' && (
              <KpiTile
                label="Sourced by us, not signed up"
                value={num(data.external)}
                icon={<CloudDownloadOutlinedIcon />}
                tone="amber"
              />
            )}
          </Box>

          <Box className="an-charts-row">
            <RankedBars
              title={`Home locality in ${data.city}`}
              note="Their home locality; the city they wrote when they gave no locality."
              rows={data.byArea}
              color={chartColor('--teal')}
              empty={`No one lists a home in ${data.city} yet`}
            />

            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">Live there, or want to move there</Typography>
                {data.total > 0 ? (
                  <PieChart
                    series={[{
                      data: [
                        { id: 0, value: data.livesThere, label: `Live in ${data.city}`, color: chartColor('--teal') },
                        { id: 1, value: data.prefersOnly, label: 'Live elsewhere', color: chartColor('--indigo') },
                      ],
                      innerRadius: 50,
                      outerRadius: 100,
                      paddingAngle: 3,
                      cornerRadius: 4,
                    }]}
                    height={240}
                    slotProps={{ legend: { direction: 'vertical', position: { vertical: 'middle', horizontal: 'end' } } }}
                  />
                ) : (
                  <EmptyState compact title={`No candidates in ${data.city} yet`} />
                )}
              </CardContent>
            </Card>
          </Box>

          <Box className="an-charts-row">
            <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
              <CardContent>
                <Typography variant="subtitle1" className="an-chart-title">Experience</Typography>
                {data.byExperience.length > 0 ? (
                  <BarChart
                    hideLegend
                    xAxis={[{ data: labels(data.byExperience), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                    series={[{ data: counts(data.byExperience), label: 'Candidates', color: chartColor('--acc') }]}
                    height={240}
                    margin={{ top: 8, right: 16, bottom: 32, left: 44 }}
                  />
                ) : (
                  <EmptyState compact title="No experience recorded" />
                )}
              </CardContent>
            </Card>

            <RankedBars
              title="Roles they want"
              note="A candidate can list several roles, so these add up to more than the total."
              rows={data.byRole}
              color={chartColor('--amber')}
              empty="No preferred roles recorded"
            />
          </Box>

          <Box className="an-charts-row">
            <RankedBars
              title="Localities they want to work in"
              note="From candidates who named any. Several each, so these add up to more than the total."
              rows={data.byPreferredLocality ?? []}
              color={chartColor('--indigo')}
              empty="No preferred localities recorded yet"
            />
            <RankedBars
              title="Skills"
              note="From candidates who listed skills. Several each, so these add up to more than the total."
              rows={data.bySkill ?? []}
              color={chartColor('--green')}
              empty="No skills recorded yet"
            />
          </Box>
        </>
      )}
    </>
  );
};

export default CandidatesByCity;
